/**
 * FidoEditor — F-012 Phase 3 (IMPL-P3-008).
 *
 * Two independent admin write paths for a business account:
 *   1. Fido form (RHF + zodResolver(updateFidoInputSchema)): the admin enters a
 *      new fido in euro + a motivazione. On submit we compute the resulting
 *      availability client-side; if it drops below zero (overdraft) we open
 *      `FidoReductionWarningModal` and wait for the GRACE/BLOCK decision before
 *      issuing the PATCH. Otherwise we PATCH directly with decision='NONE'.
 *   2. Wallet toggle: a separate button guarded by a confirm Dialog that calls
 *      the wallet-toggle endpoint (block ⇄ unblock).
 *
 * Both paths surface success/error via `sonner` toasts and rely on the mutation
 * hooks to invalidate the list + detail queries.
 *
 * Pattern: `features/profilo` (RHF + zodResolver + aria wiring),
 * `features/pratica` (sonner toast).
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Lock, Unlock } from 'lucide-react';
import { updateFidoInputSchema, type UpdateFidoInput } from '@app/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { ClienteDetailView } from '../lib/admin-client';
import { errorMessageFor } from '../lib/admin-client';
import { useFidoMutation, useWalletToggleMutation } from '../hooks/useFidoMutation';
import { centsToEuroNumber } from '../lib/format';
import { FidoReductionWarningModal } from './FidoReductionWarningModal';

interface FidoEditorProps {
  detail: ClienteDetailView;
}

/** Form shape: euro number + motivazione (decision is resolved at submit time). */
type FidoFormValues = Pick<UpdateFidoInput, 'nuovoFidoEuro' | 'motivazione'>;

export function FidoEditor({ detail }: FidoEditorProps) {
  const { account, balanceCents } = detail;
  const accountId = account.id;

  const fidoMutation = useFidoMutation(accountId);
  const walletToggle = useWalletToggleMutation(accountId);

  const currentFidoEuro = centsToEuroNumber(account.fidoLimitCents);
  const currentBalanceEuro = centsToEuroNumber(balanceCents);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FidoFormValues>({
    resolver: zodResolver(
      updateFidoInputSchema.pick({ nuovoFidoEuro: true, motivazione: true }),
    ),
    defaultValues: {
      nuovoFidoEuro: currentFidoEuro,
      motivazione: '',
    },
  });

  // Overdraft modal state.
  const [pendingValues, setPendingValues] = useState<FidoFormValues | null>(null);

  // Wallet-toggle confirm dialog state.
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);

  function applyFido(values: FidoFormValues, decision: 'GRACE' | 'BLOCK' | 'NONE') {
    fidoMutation.mutate(
      {
        nuovoFidoEuro: values.nuovoFidoEuro,
        motivazione: values.motivazione,
        decision,
      },
      {
        onSuccess: () => {
          toast.success('Fido aggiornato con successo.');
          setPendingValues(null);
        },
        onError: (err) => {
          toast.error(errorMessageFor(err));
        },
      },
    );
  }

  const onSubmit = (values: FidoFormValues) => {
    // disponibilità risultante = saldo + nuovo fido (in euro). < 0 = sconfinamento.
    const availableAfterEuro = currentBalanceEuro + values.nuovoFidoEuro;
    if (availableAfterEuro < 0) {
      setPendingValues(values);
      return;
    }
    applyFido(values, 'NONE');
  };

  const handleWalletToggle = () => {
    walletToggle.mutate(undefined, {
      onSuccess: (res) => {
        toast.success(
          res.account.walletBlocked ? 'Wallet bloccato.' : 'Wallet sbloccato.',
        );
        setConfirmToggleOpen(false);
      },
      onError: (err) => {
        toast.error(errorMessageFor(err));
      },
    });
  };

  const fidoError = errors.nuovoFidoEuro?.message;
  const motivazioneError = errors.motivazione?.message;

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------------- */}
      {/* Fido form                                                        */}
      {/* ---------------------------------------------------------------- */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="nuovoFidoEuro">Nuovo fido (€)</Label>
          <Input
            id="nuovoFidoEuro"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            aria-required="true"
            aria-invalid={Boolean(fidoError)}
            aria-describedby={fidoError ? 'nuovoFidoEuro-error' : undefined}
            {...register('nuovoFidoEuro', { valueAsNumber: true })}
          />
          {fidoError ? (
            <p
              id="nuovoFidoEuro-error"
              role="alert"
              aria-live="polite"
              className="text-sm text-destructive"
            >
              {fidoError}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="motivazione">Motivazione</Label>
          <Textarea
            id="motivazione"
            rows={3}
            placeholder="Indica il motivo della modifica del fido…"
            aria-required="true"
            aria-invalid={Boolean(motivazioneError)}
            aria-describedby={motivazioneError ? 'motivazione-error' : undefined}
            {...register('motivazione')}
          />
          {motivazioneError ? (
            <p
              id="motivazione-error"
              role="alert"
              aria-live="polite"
              className="text-sm text-destructive"
            >
              {motivazioneError}
            </p>
          ) : null}
        </div>

        <Button type="submit" disabled={fidoMutation.isPending}>
          {fidoMutation.isPending ? 'Salvataggio…' : 'Salva fido'}
        </Button>
      </form>

      {/* ---------------------------------------------------------------- */}
      {/* Wallet toggle (separate write path, confirm-gated)               */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex items-center justify-between gap-4 rounded-md border p-3">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">
            {account.walletBlocked ? 'Wallet bloccato' : 'Wallet attivo'}
          </p>
          <p className="text-xs text-muted-foreground">
            {account.walletBlocked
              ? "Sblocca per consentire all'utente di registrare nuove pratiche."
              : "Blocca per impedire all'utente di registrare nuove pratiche."}
          </p>
        </div>
        <Button
          type="button"
          variant={account.walletBlocked ? 'default' : 'destructive'}
          onClick={() => setConfirmToggleOpen(true)}
          disabled={walletToggle.isPending}
        >
          {account.walletBlocked ? (
            <>
              <Unlock className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Sblocca wallet
            </>
          ) : (
            <>
              <Lock className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Blocca wallet
            </>
          )}
        </Button>
      </div>

      {/* Wallet-toggle confirm dialog */}
      <Dialog open={confirmToggleOpen} onOpenChange={setConfirmToggleOpen}>
        <DialogContent aria-modal="true">
          <DialogHeader>
            <DialogTitle>
              {account.walletBlocked ? 'Sbloccare il wallet?' : 'Bloccare il wallet?'}
            </DialogTitle>
            <DialogDescription>
              {account.walletBlocked
                ? "L'utente potrà di nuovo registrare nuove pratiche."
                : "L'utente non potrà registrare nuove pratiche finché il wallet resta bloccato."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmToggleOpen(false)}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant={account.walletBlocked ? 'default' : 'destructive'}
              onClick={handleWalletToggle}
              disabled={walletToggle.isPending}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overdraft warning modal (grace/block decision) */}
      <FidoReductionWarningModal
        open={pendingValues !== null}
        oldFidoEuro={currentFidoEuro}
        newFidoEuro={pendingValues?.nuovoFidoEuro ?? currentFidoEuro}
        currentBalanceEuro={currentBalanceEuro}
        onConfirm={(decision) => {
          const values = pendingValues ?? getValues();
          applyFido(values, decision);
        }}
        onCancel={() => setPendingValues(null)}
      />
    </div>
  );
}
